import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MessageCircle, Phone, Send, Twitter } from "lucide-react";
import Config from "../../Config";
import siteMetadata from "../../data/siteMetadata";

/**
 * The footer is the site's main internal-linking surface, so every real public
 * route gets a link here. Nothing is invented: the address and social profiles
 * come from `siteMetadata` and `Config.socials`, and the dead "Support Us"
 * placeholder that pointed at `#` has been removed rather than left broken.
 *
 * It sits on the deep ink surface with the logo gold on the wordmark. A pale
 * footer let the page fade out at the bottom; a dark base gives it a floor, and
 * it is the one place the brand's two colours can sit at full strength together.
 */
const Footer = () => {
  const year = new Date().getFullYear();

  const socials = [
    { name: "Instagram", href: Config.socials.instagram.url, icon: Instagram },
    { name: "Facebook", href: Config.socials.facebook.url, icon: Facebook },
    { name: "X (Twitter)", href: Config.socials.twitter.url, icon: Twitter },
    { name: "LinkedIn", href: Config.socials.linkedIn.url || siteMetadata.linkedin, icon: Linkedin },
    { name: "Telegram", href: Config.socials.telegram.url, icon: Send },
  ].filter((social) => Boolean(social.href));

  const columns: { title: string; links: { name: string; href: string }[] }[] = [
    {
      title: "Learn",
      links: [
        { name: "Courses", href: "/courses" },
        { name: "Scholarships", href: "/scholarships" },
        { name: "Events", href: "/events" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/about-us" },
        { name: "Teams", href: "/teams" },
        { name: "Partner Programme", href: "/partners" },
        { name: "Partnerships", href: "/partnerships" },
        { name: "Contact Us", href: "/contact-us" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Terms of Service", href: "/terms-of-service" },
      ],
    },
  ];

  // White at 65% rather than a grey token: the surface is fixed ink in both
  // themes, so the link colour has to be too.
  const linkClass =
    "inline-flex min-h-[36px] items-center text-caption text-white/65 transition-colors duration-200 hover:text-gold";

  return (
    <footer className="bg-surface-ink text-white">
      {/* The four logo accents as a hairline rule across the top edge. */}
      <div aria-hidden="true" className="flex h-1">
        <span className="flex-1 bg-accent-orange" />
        <span className="flex-1 bg-accent-gold" />
        <span className="flex-1 bg-accent-cyan" />
        <span className="flex-1 bg-accent-blue" />
      </div>
      <div className="container-editorial py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-5 lg:gap-8">
          <div className="lg:col-span-2">
            <Link to="/" className="font-display text-h3 text-white">
              Mindalitix<span className="text-gold">.</span>
            </Link>
            <p className="mt-4 max-w-sm text-caption text-white/65">
              Empowering scholars and students with resources, community, and knowledge to excel in their academic pursuits.
            </p>

            {socials.length > 0 && (
              <ul className="mt-6 flex flex-wrap items-center gap-1">
                {socials.map(({ name, href, icon: Icon }) => (
                  <li key={name}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Mindalitix on ${name}`}
                      className="flex h-11 w-11 items-center justify-center rounded-md text-white/65 transition-colors duration-200 hover:bg-white/10 hover:text-gold"
                    >
                      <Icon size={18} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {columns.map((column) => (
            <nav key={column.title} aria-label={column.title}>
              <h2 className="font-sans text-overline font-semibold uppercase text-white">
                {column.title}
              </h2>
              <ul className="mt-4 space-y-1">
                {column.links.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className={linkClass}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <div>
            <h2 className="font-sans text-overline font-semibold uppercase text-white">
              Contact
            </h2>
            <ul className="mt-4 space-y-1">
              <li>
                <a href={`mailto:${siteMetadata.email}`} className={linkClass}>
                  <Mail size={16} aria-hidden="true" className="mr-2 shrink-0" />
                  {siteMetadata.email}
                </a>
              </li>
              <li>
                <a href="tel:+2347026534699" className={linkClass}>
                  <Phone size={16} aria-hidden="true" className="mr-2 shrink-0" />
                  +234 (7026) 534699
                </a>
              </li>
              <li>
                <Link to="/contact-us" className={linkClass}>
                  <MessageCircle size={16} aria-hidden="true" className="mr-2 shrink-0" />
                  Send us a message
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-caption text-white/50">
            &copy; {year} Mindalitix. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6">
            <Link to="/privacy-policy" className={linkClass}>
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className={linkClass}>
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer
